import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { AdminMediaCard } from "@/components/MediaCard";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { resolveReviewStage } from "@/domains/LibraryView/reviewStage";
import { useReviewUrlState } from "@/core/navigation/useReviewUrlState";
import { ShareWithClientModal } from "@/domains/PostCreator/ShareWithClientModal";
import { LibraryLayout, ContentType } from "./LibraryLayout";
import { VideoPreviewModal } from "./VideoPreviewModal";
import { VideoReviewModal } from "./VideoReviewModal";

export const LongVideoLibrary: React.FC = () => {
  const { selectedClient } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedVideo, setSelectedVideo] = useState<MediaItem | null>(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const review = useReviewUrlState();

  const handleContentTypeChange = (contentType: ContentType) => {
    const pathMap: Record<ContentType, string> = {
      images: "../photos",
      "post-videos": "../post-videos",
      stories: "../stories",
      reels: "../reels",
      videos: "../videos",
      audio: "../audio",
      assets: "../assets",
    };
    navigate(pathMap[contentType]);
  };

  const handleVideoClick = (video: MediaItem) => {
    setSelectedVideo(video);
    setIsPlayerOpen(true);
  };

  const handlePlayerClose = () => {
    setIsPlayerOpen(false);
    setSelectedVideo(null);
  };

  const handleEnterReviewMode = (selectedIds: number[]) => {
    review.open(selectedIds.length > 0 ? selectedIds[0] : undefined);
  };

  const renderGrid = (
    items: MediaItem[],
    handleStageChange: (updatedItem: MediaItem, oldStage: MediaStage) => void,
    _handleItemClick?: (item: MediaItem) => void,
    selectionMode?: {
      isActive: boolean;
      selectedIds: number[];
      onToggle: (id: number, event: React.MouseEvent) => void;
    },
    onAddToCollection?: (mediaItemId: number) => void,
  ) => (
    <>
      {items.map((mediaItem) => (
        <AdminMediaCard
          key={mediaItem.id}
          mediaItem={mediaItem}
          clientId={selectedClient?.id || ""}
          aspectRatio="9:16"
          onClick={() => handleVideoClick(mediaItem)}
          onStageChange={handleStageChange}
          onMediaItemDeleted={() => {}}
          selectionMode={selectionMode}
          onAddToCollection={onAddToCollection}
        />
      ))}
    </>
  );

  return (
    <LibraryLayout
      contentTypes={["reels"]}
      activeContentType="reels"
      onContentTypeChange={handleContentTypeChange}
      renderGrid={renderGrid}
      onShareWithClient={() => setIsShareModalOpen(true)}
      onEnterReviewMode={handleEnterReviewMode}
      extraContent={
        <>
          {selectedVideo && (
            <VideoPreviewModal
              mediaItem={selectedVideo}
              isOpen={isPlayerOpen}
              onClose={handlePlayerClose}
              clientId={selectedClient?.id || ""}
            />
          )}
          {selectedClient && (
            <ShareWithClientModal
              isOpen={isShareModalOpen}
              onClose={() => setIsShareModalOpen(false)}
              clientId={selectedClient.id}
              clientName={selectedClient.clientName}
              redirectPath={`/client/${selectedClient.id}/reel`}
            />
          )}
          {selectedClient && (
            <VideoReviewModal
              isOpen={review.isOpen}
              onClose={review.close}
              clientId={selectedClient.id}
              stage={resolveReviewStage(searchParams.get("stage"))}
              contentType="reels"
              initialVideoId={review.initialMediaId}
              onCurrentItemChange={review.setCurrentItem}
            />
          )}
        </>
      }
    />
  );
};
